import React, { useState, useEffect } from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  Image,
  Text,
  ActivityIndicator,
  TouchableOpacity,
  Modal,
  Dimensions,
} from 'react-native';
import { useFonts } from 'expo-font';
import imageCompression from 'browser-image-compression';
import { SafeAreaView } from 'react-native';
import { db } from '../logic/firebase'; // Assicurati che il percorso sia corretto
import { ref, onValue } from 'firebase/database';

const { width } = Dimensions.get("window");

// Funzione helper per comprimere l'immagine in maniera "leggera"
// Impostazioni: maxSizeMB impostato a 0.5 e maxWidthOrHeight a 400
const compressImageUrl = async (url) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const file = new File([blob], "image.jpg", { type: blob.type });
    const options = {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 400,
      useWebWorker: true,
    };
    const compressedFile = await imageCompression(file, options);
    return URL.createObjectURL(compressedFile);
  } catch (error) {
    console.error("Error compressing image:", error);
    return url;
  }
};

const GalleryScreen = () => {
  // Caricamento del font personalizzato
  const [fontsLoaded] = useFonts({
    'MyCustomFont': require('../../assets/font.ttf'),
  });

  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [modalImageLoading, setModalImageLoading] = useState(false);

  // Carica le foto dal nodo "PhotoData" di Firebase, ordina per data di creazione decrescente
  // e processa le immagini in modo sequenziale in modo che le più recenti vengano caricate per prime.
  useEffect(() => {
    const photosRef = ref(db, 'PhotoData');
    const unsubscribe = onValue(
      photosRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          // Ordina le chiavi in base alla data (dal più recente al meno recente)
          const keysSorted = Object.keys(data).sort(
            (a, b) => new Date(data[b].createdAt) - new Date(data[a].createdAt)
          );
          // Processa le foto in modo sequenziale
          (async () => {
            let loadedPhotos = [];
            for (const key of keysSorted) {
              const item = data[key];
              const originalUrl = item.imageUrl;
              const compressedUrl = await compressImageUrl(originalUrl);
              loadedPhotos.push({
                id: key,
                originalUrl,             // URL originale per il popup
                imageUrl: compressedUrl,  // URL compressa per la galleria
                description: item.description,
                createdAt: item.createdAt,
                updatedAt: item.updatedAt,
              });
              // Aggiorna lo stato progressivamente
              setPhotos([...loadedPhotos]);
            }
            setLoading(false);
          })();
        } else {
          setLoading(false);
        }
      },
      (error) => {
        console.error("Error fetching photos from Firebase:", error);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  // Gestione pressione su immagine per apertura del popup
  const handlePress = (photo) => {
    setModalImageLoading(true);
    setSelectedPhoto(photo);
    setModalVisible(true);
  };

  // Se i font non sono ancora caricati, mostra uno spinner a schermo intero
  if (!fontsLoaded) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#bb86fc" />
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.screenContainer}>
      {/* Header sempre visibile */}
      <View style={styles.header}>
        <Text style={[styles.headerTitle, { fontFamily: 'MyCustomFont' }]}>
          Galleria Moderna
        </Text>
      </View>

      {/* Se non ci sono ancora foto e ancora in caricamento, mostra uno spinner */}
      {loading && photos.length === 0 ? (
        <View style={styles.loaderContainer}>
          <ActivityIndicator size="large" color="#bb86fc" />
        </View>
      ) : (
        <FlatList
          data={photos}
          keyExtractor={(item) => item.id}
          numColumns={2} // Due immagini per riga
          contentContainerStyle={styles.flatListContent} // Aggiunge spaziatura (verticale e orizzontale)
          renderItem={({ item }) => (
            <TouchableOpacity onPress={() => handlePress(item)} style={styles.imageContainer}>
              <Image source={{ uri: item.imageUrl }} style={styles.galleryImage} />
            </TouchableOpacity>
          )}
        />
      )}

      {/* Popup per visualizzare l'immagine originale e la descrizione */}
      <Modal
        visible={modalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            {selectedPhoto && (
              <>
                <View style={styles.modalImageContainer}>
                  <Image
                    source={{ uri: selectedPhoto.originalUrl }}
                    style={styles.modalImage}
                    resizeMode="contain"
                    onLoadEnd={() => setModalImageLoading(false)}
                  />
                  {modalImageLoading && (
                    <View style={styles.modalSpinnerOverlay}>
                      <ActivityIndicator size="large" color="#bb86fc" />
                    </View>
                  )}
                </View>
                <Text style={[styles.modalDescription, { fontFamily: 'MyCustomFont' }]}>
                  {selectedPhoto.description || "Nessuna descrizione disponibile"}
                </Text>
                <TouchableOpacity
                  style={styles.closeButton}
                  onPress={() => setModalVisible(false)}
                >
                  <Text style={[styles.closeButtonText, { fontFamily: 'MyCustomFont' }]}>
                    Chiudi
                  </Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  screenContainer: {
    flex: 1,
    backgroundColor: "#121212",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#121212",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  header: {
    paddingVertical: 20,
    backgroundColor: "#1f1f1f",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#333",
  },
  headerTitle: {
    color: "#fff",
    fontSize: 24,
    fontWeight: "bold",
  },
  flatListContent: {
    paddingTop: 15,      // Spazio tra header e immagini
    paddingBottom: 10,
    paddingHorizontal: 10, // Spazio aggiunto ai margini sinistra e destra
  },
  imageContainer: {
    flex: 1,
    margin: 5,
    borderRadius: 12,
    overflow: "hidden",
  },
  galleryImage: {
    width: "100%",
    height: 150,  // Altezza fissa per mantenere la forma rettangolare
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.8)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "#1f1f1f",
    padding: 20,
    borderRadius: 10,
    alignItems: "center",
    width: "90%",
  },
  modalImageContainer: {
    width: "100%",
    height: 300,
    borderRadius: 10,
    marginBottom: 10,
    position: "relative",
  },
  modalImage: {
    width: "100%",
    height: "100%",
    borderRadius: 10,
  },
  modalSpinnerOverlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: "center",
    alignItems: "center",
  },
  modalDescription: {
    color: "#fff",
    fontSize: 16,
    textAlign: "center",
  },
  closeButton: {
    marginTop: 10,
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: "#bb86fc",
    borderRadius: 5,
  },
  closeButtonText: {
    color: "#fff",
    fontWeight: "bold",
  },
});

export default GalleryScreen;
